import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Hero } from './components/Hero';
import { FilterBar } from './components/FilterBar';
import { ProductGrid } from './components/ProductGrid';
import { ProductDetailModal } from './components/ProductDetailModal';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner.jsx';
import Nav from '../Nav/Nav';
import Footer from '../Footer/Footer';
import './accessoriesStyles/app.css';
import ACCESSORIES_PRODUCTS from './data/accessoriesData.js';
import MARSHALL_ACCESSORIES_PRODUCTS from './data/marshallAccessoriesData.js';

export default function Accessories() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Combine all products
  const allProducts = useMemo(() => {
    return [...ACCESSORIES_PRODUCTS.filter((p) => p.brand !== 'Marshall'), ...MARSHALL_ACCESSORIES_PRODUCTS];
  }, []);
  const [filters, setFilters] = useState({
    categories: [],
    brands: [],
    priceRange: [0, 120000],
    sortBy: 'none',
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Extract unique categories and brands
  const availableCategories = useMemo(
    () => Array.from(new Set(allProducts.map(p => p.category))),
    [allProducts]
  );

  const availableBrands = useMemo(
    () => Array.from(new Set(allProducts.map(p => p.brand))),
    [allProducts]
  );

  // Filter products based on current filters
  const filteredProducts = useMemo(() => {
    let products = allProducts.filter(product => {
      // Category filter
      if (filters.categories.length > 0 && !filters.categories.includes(product.category)) {
        return false;
      }

      // Brand filter
      if (filters.brands.length > 0 && !filters.brands.includes(product.brand)) {
        return false;
      }

      // Price range filter
      if (product.price < filters.priceRange[0] || product.price > filters.priceRange[1]) {
        return false;
      }

      return true;
    });

    // Apply sorting
    if (filters.sortBy === 'lowToHigh') {
      products = [...products].sort((a, b) => a.price - b.price);
    } else if (filters.sortBy === 'highToLow') {
      products = [...products].sort((a, b) => b.price - a.price);
    }

    return products;
  }, [filters, allProducts]);

  const handleQuickAdd = (product) => {
    toast.success(`Added ${product.name} to cart!`, {
      description: `${product.brand} - ₹${product.price.toLocaleString('en-IN')}`,
    });
  };

  const handleViewDetails = (product) => {
    if (product.comingSoon) {
      toast.info('This product is coming soon! Stay tuned for updates.');
      return;
    }
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Remove product query parameter
    searchParams.delete('product');
    setSearchParams(searchParams);
    // Delay clearing the product to allow exit animation
    setTimeout(() => setSelectedProduct(null), 300);
  };

  const handleMenuItemClick = (menuItem) => {
    // Set filters based on menu item click
    const newFilters = {
      categories: [menuItem.category],
      brands: [menuItem.brand],
      priceRange: [0, 120000],
      sortBy: 'none',
    };
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    // Clear all filters to show all products
    setFilters({
      categories: [],
      brands: [],
      priceRange: [0, 120000],
      sortBy: 'none',
    });
  };

  // Handle product query parameter on mount
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId) {
      const product = allProducts.find(p => p.id === productId);
      if (product) {
        handleViewDetails(product);
      }
    }
  }, [searchParams, allProducts]);

  return (
    <div className="app-container">
      <Nav theme="light" />
      <Toaster position="bottom-right" />
      
      {/* Hero Section */}
      <Hero onMenuItemClick={handleMenuItemClick} onClearFilters={handleClearFilters} filters={filters} />

      {/* Filter Bar */}
      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        availableCategories={availableCategories}
        availableBrands={availableBrands}
      />

      {/* Product Grid */}
      <div className="product-grid-container">
        <ProductGrid 
          products={filteredProducts} 
          onQuickAdd={handleQuickAdd}
          onViewDetails={handleViewDetails}
        />
      </div>

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
